// src/pages/redress/LedgerScreen.tsx
//
// Per-case ledger screen (Milestone 6). Sits alongside Liability,
// Calculations and Offers as a sub-tab of RedressTab.
//
// Rendering:
//   * Top summary panel populated from ``compute_case_ledger_totals``
//     (Received / Arrears / CL fees / VAT / Paid to client / Balance).
//   * Chronological table of entries with delete (reversal) action.
//   * "+ Add entry" modal for manual adjustments.
//
// The delete action doesn't remove anything -- the backend appends a
// reversal entry of equal-and-opposite direction (see
// ``redress_ledger_service.remove_ledger_entry``). Original and reversal
// both show in the table so ops can follow the trail.

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Agreement,
  LedgerDirection,
  LedgerEntry,
  LedgerEntryType,
  LedgerTotals,
  RedressCase,
  redressApi,
} from '../../api/redress';
import {
  applyDisabled,
  bannerDangerStyle,
  bannerWarnStyle,
  buttonDangerOutline,
  buttonNeutral,
  buttonPrimary,
  buttonSecondary,
  buttonSmall,
  colors,
  fieldsetStyle,
  inputStyle,
  labelStackStyle,
  legendStyle,
  pillWarning,
  tableStyle,
  tdStyle,
  thStyle,
} from './styles';

interface LedgerScreenProps {
  claimId: string;
  redressCase: RedressCase;
  agreement: Agreement;
  agreementId: string;
  onCaseUpdated: (c: RedressCase) => void;
}

// Types that default to 'in' (money into CL trust). Everything else defaults
// to 'out'. Operators can override in the add-entry modal.
const IN_BY_DEFAULT: ReadonlySet<LedgerEntryType> = new Set<LedgerEntryType>([
  'offer_received',
]);

const TYPE_OPTIONS: Array<{ value: LedgerEntryType; label: string }> = [
  { value: 'offer_received', label: 'Offer received' },
  { value: 'arrears_deducted', label: 'Arrears deducted' },
  { value: 'cl_deduction', label: 'CL deduction' },
  { value: 'cl_fee_shortfall', label: 'CL fee shortfall' },
  { value: 'statutory_bill', label: 'Statutory bill' },
  { value: 'vat', label: 'VAT' },
  { value: 'paid_to_client', label: 'Paid to client' },
  { value: 'late_interest', label: 'Late interest' },
  { value: 'adjustment', label: 'Adjustment' },
];

function typeLabel(t: LedgerEntryType): string {
  return TYPE_OPTIONS.find((o) => o.value === t)?.label ?? t;
}

function formatMoney(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '-';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatTs(ts: string | null | undefined): string {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function extractError(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { error?: string; reason?: string } };
    message?: string;
  };
  return (
    ax.response?.data?.error ||
    ax.response?.data?.reason ||
    ax.message ||
    fallback
  );
}

export default function LedgerScreen({
  claimId,
  redressCase,
  agreementId,
  onCaseUpdated,
}: LedgerScreenProps): React.ReactElement {
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [totals, setTotals] = useState<LedgerTotals | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showOnlyAgreement, setShowOnlyAgreement] = useState<boolean>(true);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await redressApi.getLedger(claimId, redressCase.id, agreementId);
      setEntries(res.entries);
      setTotals(res.totals);
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to load ledger'));
    } finally {
      setLoading(false);
    }
  }, [claimId, redressCase.id, agreementId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleAfterMutation = useCallback(
    (next: { case: RedressCase; totals: LedgerTotals }) => {
      onCaseUpdated(next.case);
      setTotals(next.totals);
      setEntries(
        (next.case.ledger || []) as unknown as LedgerEntry[],
      );
    },
    [onCaseUpdated],
  );

  const handleDelete = async (entryId: string): Promise<void> => {
    if (
      !window.confirm(
        'Append an equal-and-opposite reversal entry? The original entry remains for audit.',
      )
    ) {
      return;
    }
    setBusy(`delete-${entryId}`);
    setError(null);
    try {
      const res = await redressApi.removeLedgerEntry(
        claimId, redressCase.id, agreementId, entryId,
      );
      handleAfterMutation(res);
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to reverse entry'));
    } finally {
      setBusy(null);
    }
  };

  // --- Render ---------------------------------------------------------------

  // Chase-remainder detection: any cl_fee_shortfall entry with chaseRemainder flag.
  const hasShortfall = entries.some(
    (e) => e.type === 'cl_fee_shortfall' && e.chaseRemainder,
  );

  const sortedEntries = useMemo(() => {
    const filtered = showOnlyAgreement
      ? entries.filter(
          (e) => e.agreementId === agreementId || e.agreementId == null,
        )
      : entries;
    return [...filtered].sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
  }, [entries, showOnlyAgreement, agreementId]);

  return (
    <div>
      {/* Under-recovery banner */}
      {hasShortfall ? (
        <div style={bannerWarnStyle} role="alert">
          <div>
            <strong>Under-recovery flagged.</strong> Complex Law received less than the
            fee due after arrears. The banked partial is recorded; the difference is a
            shortfall receivable to be chased.
          </div>
        </div>
      ) : null}

      {/* Summary panel */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Summary</legend>
        {loading ? (
          <div style={{ color: colors.neutral500, fontSize: 14 }}>
            Loading ledger…
          </div>
        ) : totals === null ? (
          <div style={{ color: colors.neutral500, fontSize: 14 }}>
            No totals available.
          </div>
        ) : (
          <div style={summaryGridStyle}>
            <SummaryCell
              label="Received from lender"
              value={totals.totalReceivedFromLender}
            />
            <SummaryCell
              label="Arrears deducted"
              value={totals.totalArrearsDeducted}
            />
            <SummaryCell label="CL fees" value={totals.totalClFees} />
            <SummaryCell
              label="CL fee shortfall (chase)"
              value={totals.totalClFeeShortfall ?? 0}
              emphasis={totals.totalClFeeShortfall != null && totals.totalClFeeShortfall > 0}
              warning={totals.totalClFeeShortfall != null && totals.totalClFeeShortfall > 0}
            />
            <SummaryCell label="VAT" value={totals.totalVat} />
            <SummaryCell
              label="Paid to client"
              value={totals.totalPaidToClient}
            />
            <SummaryCell
              label="Late interest"
              value={totals.totalLateInterest}
            />
            {/* F3 polish: the backend excludes accrual entries
                (cl_fee_shortfall receivables) from ``balance`` — this is
                CASH in the trust account, so label it as such. */}
            <SummaryCell
              label="Balance in trust (cash)"
              value={totals.balance}
              emphasis
            />
          </div>
        )}
      </fieldset>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          style={buttonPrimary}
        >
          + Add entry
        </button>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          style={applyDisabled(buttonSecondary, loading)}
          aria-busy={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: colors.neutral600,
            cursor: 'pointer',
            marginLeft: 8,
          }}
        >
          <input
            type="checkbox"
            checked={showOnlyAgreement}
            onChange={(e) => setShowOnlyAgreement(e.target.checked)}
          />
          Show only this agreement
        </label>
      </div>

      {/* Entries table */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Entries</legend>
        {sortedEntries.length === 0 ? (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: colors.neutral500,
              background: colors.neutral50,
              border: `1px dashed ${colors.neutral200}`,
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 6 }} aria-hidden="true">
              ◯
            </div>
            <div style={{ fontWeight: 500, color: colors.neutral700 }}>
              No ledger entries yet
            </div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              Entries will appear automatically once the offer is marked paid,
              or you can add a manual adjustment above.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Label</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                  <th style={thStyle}>Reference</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map((entry) => {
                  const directionColor =
                    entry.direction === 'in' ? colors.success : colors.danger;
                  const sign = entry.direction === 'in' ? '+' : '−';
                  const reversed = Boolean(entry.reversedAt);
                  const isReversal = Boolean(entry.reversedEntryId);
                  return (
                    <tr
                      key={entry.id}
                      style={{
                        opacity: reversed ? 0.55 : 1,
                      }}
                    >
                      <td
                        style={{
                          ...tdStyle,
                          color: colors.neutral600,
                          fontSize: 13,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatTs(entry.ts)}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          fontSize: 13,
                          color: colors.neutral700,
                        }}
                      >
                        {typeLabel(entry.type)}
                      </td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            textDecoration: reversed ? 'line-through' : 'none',
                          }}
                        >
                          {entry.label}
                        </span>
                        {entry.chaseRemainder ? (
                          <span style={{ ...pillWarning, marginLeft: 8 }}>
                            Chase remainder
                          </span>
                        ) : null}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          textAlign: 'right',
                          color: directionColor,
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                          textDecoration: reversed ? 'line-through' : 'none',
                        }}
                      >
                        {sign} £&nbsp;{formatMoney(entry.amount)}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 13, color: colors.neutral600 }}>
                        {entry.reference ?? '—'}
                      </td>
                      <td style={tdStyle}>
                        {!reversed && !isReversal ? (
                          <button
                            type="button"
                            onClick={() => void handleDelete(entry.id)}
                            disabled={busy !== null}
                            style={applyDisabled(
                              { ...buttonDangerOutline, ...buttonSmall },
                              busy !== null,
                            )}
                            aria-busy={busy === `delete-${entry.id}`}
                          >
                            {busy === `delete-${entry.id}`
                              ? 'Reversing…'
                              : 'Reverse'}
                          </button>
                        ) : reversed ? (
                          <em
                            style={{ fontSize: 12, color: colors.neutral500 }}
                          >
                            reversed
                          </em>
                        ) : (
                          <em
                            style={{ fontSize: 12, color: colors.neutral500 }}
                          >
                            reversal
                          </em>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </fieldset>

      {error !== null && (
        <div style={bannerDangerStyle} role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {showModal && (
        <AddEntryModal
          onClose={() => setShowModal(false)}
          onSubmit={async (draft) => {
            setBusy('add-entry');
            setError(null);
            try {
              const res = await redressApi.addLedgerEntry(
                claimId, redressCase.id, agreementId, draft,
              );
              handleAfterMutation(res);
              setShowModal(false);
            } catch (err: unknown) {
              setError(extractError(err, 'Failed to add entry'));
            } finally {
              setBusy(null);
            }
          }}
          busy={busy === 'add-entry'}
        />
      )}
    </div>
  );
}

// --- Summary cell -----------------------------------------------------------

interface SummaryCellProps {
  label: string;
  value: number;
  emphasis?: boolean;
  warning?: boolean;
}

function SummaryCell({
  label,
  value,
  emphasis,
  warning,
}: SummaryCellProps): React.ReactElement {
  const isNegative = value < 0;
  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${
          warning
            ? colors.danger
            : emphasis
            ? colors.warningBorder
            : colors.neutral200
        }`,
        borderRadius: 8,
        background: warning
          ? colors.dangerLight
          : emphasis
          ? colors.warningLight
          : colors.neutral50,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: colors.neutral500,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: emphasis ? 22 : 18,
          fontWeight: emphasis ? 700 : 600,
          color: isNegative ? colors.danger : colors.neutral900,
          fontVariantNumeric: 'tabular-nums',
          marginTop: 4,
        }}
      >
        £&nbsp;{formatMoney(value)}
      </div>
    </div>
  );
}

// --- Add-entry modal --------------------------------------------------------

interface AddEntryDraft {
  type: LedgerEntryType;
  direction: LedgerDirection;
  amount: number;
  label?: string;
  reference?: string;
}

interface AddEntryModalProps {
  onClose: () => void;
  onSubmit: (draft: AddEntryDraft) => Promise<void>;
  busy: boolean;
}

function AddEntryModal({
  onClose,
  onSubmit,
  busy,
}: AddEntryModalProps): React.ReactElement {
  const [type, setType] = useState<LedgerEntryType>('adjustment');
  const [direction, setDirection] = useState<LedgerDirection>('in');
  const [amount, setAmount] = useState<string>('');
  const [label, setLabel] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Default direction from type (for ambiguous types operators can still flip).
  const onTypeChange = (next: LedgerEntryType) => {
    setType(next);
    setDirection(IN_BY_DEFAULT.has(next) ? 'in' : 'out');
  };

  const handleSubmit = async (): Promise<void> => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) {
      setValidationError('Amount must be a positive number');
      return;
    }
    setValidationError(null);
    await onSubmit({
      type,
      direction,
      amount: n,
      label: label || undefined,
      reference: reference || undefined,
    });
  };

  return (
    <div style={modalBackdropStyle} role="dialog" aria-modal="true" aria-label="Add ledger entry">
      <div style={modalStyle}>
        <h4
          style={{
            margin: '0 0 16px',
            fontSize: 18,
            fontWeight: 600,
            color: colors.neutral900,
          }}
        >
          Add ledger entry
        </h4>
        <label style={labelStackStyle}>
          Type
          <select
            value={type}
            onChange={(e) => onTypeChange(e.target.value as LedgerEntryType)}
            style={inputStyle}
          >
            {TYPE_OPTIONS.map(({ value, label: l }) => (
              <option key={value} value={value}>{l}</option>
            ))}
          </select>
        </label>
        <label style={labelStackStyle}>
          Direction
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as LedgerDirection)}
            style={inputStyle}
          >
            <option value="in">In (money received)</option>
            <option value="out">Out (money leaving)</option>
          </select>
        </label>
        <label style={labelStackStyle}>
          Amount (£)
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={labelStackStyle}>
          Label
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={inputStyle}
            placeholder="Defaults to humanised type"
          />
        </label>
        <label style={labelStackStyle}>
          Reference
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            style={inputStyle}
            placeholder="Bank reference or memo"
          />
        </label>
        {validationError !== null && (
          <div
            style={{
              color: colors.danger,
              marginTop: 8,
              fontSize: 13,
            }}
            role="alert"
          >
            {validationError}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 16,
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={applyDisabled(buttonNeutral, busy)}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={busy}
            style={applyDisabled(buttonPrimary, busy)}
            aria-busy={busy}
          >
            {busy ? 'Saving…' : 'Save entry'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Inline styles ----------------------------------------------------------

const summaryGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 10,
};

const modalBackdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20000,
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  background: colors.white,
  borderRadius: 12,
  padding: 24,
  minWidth: 360,
  maxWidth: 480,
  width: '100%',
  boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
};
